import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { authService } from '../services/authService';
import ListPagination from './ListPagination';

const MANAGE_PAGE_SIZE = 15;

const ENTITY_CONFIG = {
  companies: { label: 'شرکت', api: 'companies' },
  products: { label: 'محصول', api: 'products' },
  'product-groups': { label: 'گروه محصول', api: 'product-groups' },
  'document-types': { label: 'نوع سند', api: 'document-types' },
  locations: { label: 'محل', api: 'locations' },
  shifts: { label: 'شیفت', api: 'shifts' },
  units: { label: 'واحد سنجش', api: 'units' },
};

const ManageList = () => {
  const { entity } = useParams();
  const navigate = useNavigate();
  const user = authService.getCurrentUser() || {};
  const [items, setItems] = useState([]);
  const [editMap, setEditMap] = useState({});
  const [newName, setNewName] = useState('');
  const [groups, setGroups] = useState([]); // for product grouping
  const [productEdits, setProductEdits] = useState({}); // {id: {name, groupId}}
  const [newProductGroupId, setNewProductGroupId] = useState('');
  const [groupEdits, setGroupEdits] = useState({}); // {id: {name, requiresAssay}}
  const [newGroupRequiresAssay, setNewGroupRequiresAssay] = useState(false);
  const [quickGroupName, setQuickGroupName] = useState('');
  const [quickGroupRequiresAssay, setQuickGroupRequiresAssay] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [page, setPage] = useState(0);

  const showMessage = (msg, severity = 'success') => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const config = ENTITY_CONFIG[entity];

  useEffect(() => {
    setPage(0);
  }, [entity]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(items.length / MANAGE_PAGE_SIZE) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [items.length, page]);

  const pagedItems = useMemo(() => {
    const start = page * MANAGE_PAGE_SIZE;
    return items.slice(start, start + MANAGE_PAGE_SIZE);
  }, [items, page]);

  useEffect(() => {
    if (!config) return;
    const fetchItems = async () => {
      try {
        if (config.api) {
          // Fetch entity list
          const res = await axios.get(`${API_BASE_URL}/${config.api}/`);
          const data = res.data || [];
          setItems(data);
          // If managing products, also fetch product groups and initialize product edit state
          if (entity === 'products') {
            const groupsRes = await axios.get(`${API_BASE_URL}/product-groups/`);
            setGroups(groupsRes.data || []);
            const pe = {};
            data.forEach((p) => {
              pe[p.id] = {
                name: p.name || '',
                groupId: p.groupId ?? '',
              };
            });
            setProductEdits(pe);
          } else if (entity === 'product-groups') {
            const ge = {};
            data.forEach((g) => {
              ge[g.id] = {
                name: g.name || '',
                requiresAssay: !!g.requiresAssay,
              };
            });
            setGroupEdits(ge);
          } else {
            const m = {};
            data.forEach((it) => (m[it.id] = it.name));
            setEditMap(m);
          }
        } else {
          // Use API for all entities including units
          const res = await axios.get(`${API_BASE_URL}/${config.api}/`);
          const data = res.data || [];
          setItems(data);
          const m = {};
          data.forEach((it) => (m[it.id] = it.name));
          setEditMap(m);
        }
      } catch (e) {
        showMessage('خطا در دریافت اطلاعات', 'error');
      }
    };
    fetchItems();
  }, [config, entity]);

  if (!config) {
    return (
      <Container maxWidth="md">
        <Typography sx={{ mt: 6, textAlign: 'center', color: '#e44258' }}>
          موجودیت نامعتبر است
        </Typography>
      </Container>
    );
  }

  const handleSave = async (id) => {
    try {
      if (config.api) {
        const token = localStorage.getItem('token');
        if (!token) {
          showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
          return;
        }
        let payload = {};
        if (entity === 'products') {
          const current = productEdits[id] || {};
          const name = (current.name || '').trim();
          if (!name) return;
          payload = {
            name,
            groupId: current.groupId || null,
          };
        } else if (entity === 'product-groups') {
          const current = groupEdits[id] || {};
          const name = (current.name || '').trim();
          if (!name) return;
          payload = {
            name,
            requiresAssay: !!current.requiresAssay,
          };
        } else {
          const name = (editMap[id] || '').trim();
          if (!name) return;
          payload = { name };
        }
        const res = await axios.put(
          `${API_BASE_URL}/${config.api}/${id}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
        setItems((prev) => prev.map((it) => (it.id === id ? res.data : it)));
        
        // If we're updating products or product groups, refresh the products list
        // to update the requiresAssay field in real-time
        if (entity === 'products' || entity === 'product-groups') {
          try {
            const productsRes = await axios.get(`${API_BASE_URL}/products/`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setItems(productsRes.data);
          } catch (refreshError) {
            console.warn('Failed to refresh products list:', refreshError);
          }
        }
      } else {
        // Use API for all entities including units
        const token = localStorage.getItem('token');
        if (!token) {
          showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
          return;
        }
        const name = (editMap[id] || '').trim();
        if (!name) return;
        await axios.patch(
          `${API_BASE_URL}/${config.api}/${id}/`,
          { name },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
        setItems((prev) => prev.map((it) => (it.id === id ? { ...it, name } : it)));
        setEditMap((prev) => {
          const cp = { ...prev };
          delete cp[id];
          cp[name] = name;
          return cp;
        });
      }
      showMessage('ذخیره شد', 'success');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'خطا در ذخیره';
      showMessage(msg, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این مورد را حذف کنید؟')) {
      return;
    }
    
    try {
      if (config.api) {
        const token = localStorage.getItem('token');
        if (!token) {
          showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
          return;
        }
        await axios.delete(`${API_BASE_URL}/${config.api}/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        setItems((prev) => prev.filter((it) => it.id !== id));
      } else {
        // Use API for all entities including units
        const token = localStorage.getItem('token');
        if (!token) {
          showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
          return;
        }
        await axios.delete(`${API_BASE_URL}/${config.api}/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        setItems((prev) => prev.filter((it) => it.id !== id));
      }
      showMessage('حذف شد', 'success');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'خطا در حذف';
      showMessage(msg, 'error');
    }
  };

  const handleAdd = async () => {
    try {
      if (config.api) {
        const token = localStorage.getItem('token');
        if (!token) {
          showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
          return;
        }
        let payload = {};
        if (entity === 'products') {
          const name = newName.trim();
          if (!name) return;
          payload = {
            name,
            groupId: newProductGroupId || null,
          };
        } else if (entity === 'product-groups') {
          const name = newName.trim();
          if (!name) return;
          payload = { name, requiresAssay: !!newGroupRequiresAssay };
        } else {
          const name = newName.trim();
          if (!name) return;
          payload = { name };
        }
        const res = await axios.post(
          `${API_BASE_URL}/${config.api}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
        setItems((prev) => [...prev, res.data]);
        if (entity === 'products') {
          setProductEdits((prev) => ({
            ...prev,
            [res.data.id]: {
              name: res.data.name || '',
              groupId: res.data.groupId ?? '',
            },
          }));
        } else if (entity === 'product-groups') {
          setGroupEdits((prev) => ({
            ...prev,
            [res.data.id]: {
              name: res.data.name || '',
              requiresAssay: !!res.data.requiresAssay,
            },
          }));
        } else {
          setEditMap((prev) => ({ ...prev, [res.data.id]: res.data.name }));
        }
      } else {
        // Use API for all entities including units
        const token = localStorage.getItem('token');
        if (!token) {
          showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
          return;
        }
        const name = newName.trim();
        if (!name) return;
        const res = await axios.post(
          `${API_BASE_URL}/${config.api}/`,
          { name },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
        );
        setItems((prev) => [...prev, res.data]);
        setEditMap((prev) => ({ ...prev, [res.data.id]: res.data.name }));
      }
      setNewName('');
      setNewProductGroupId('');
      // no assay in management UI
      setNewGroupRequiresAssay(false);
      showMessage('افزوده شد', 'success');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'خطا در افزودن';
      showMessage(msg, 'error');
    }
  };

  const handleQuickAddGroup = async () => {
    try {
      const name = quickGroupName.trim();
      if (!name) return;
      const token = localStorage.getItem('token');
      if (!token) {
        showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
        return;
      }
      const res = await axios.post(
        `${API_BASE_URL}/product-groups/`,
        { name, requiresAssay: !!quickGroupRequiresAssay },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 }
      );
      const created = res.data;
      setGroups((prev) => [...prev, created]);
      setNewProductGroupId(created.id);
      setQuickGroupName('');
      setQuickGroupRequiresAssay(false);
      showMessage('گروه افزوده شد', 'success');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'خطا در افزودن گروه';
      showMessage(msg, 'error');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      if (!groupId) return;
      if (!window.confirm('آیا از حذف این گروه مطمئن هستید؟')) return;
      const token = localStorage.getItem('token');
      if (!token) {
        showMessage('توکن نامعتبر است. دوباره وارد شوید.', 'error');
        return;
      }
      await axios.delete(`${API_BASE_URL}/product-groups/${groupId}/`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
      // Clear selection where applicable
      setNewProductGroupId((prev) => (prev === groupId ? '' : prev));
      setProductEdits((prev) => {
        const cp = { ...prev };
        Object.keys(cp).forEach((pid) => {
          if (cp[pid]?.groupId === groupId) {
            cp[pid] = { ...cp[pid], groupId: '' };
          }
        });
        return cp;
      });
      showMessage('گروه حذف شد', 'success');
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'خطا در حذف گروه';
      showMessage(msg, 'error');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #00a86a 0%, #1a335b 100%)',
          borderRadius: '16px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          مدیریت {config.label}
        </Typography>
      </Box>

      {user.role !== 'admin' ? (
        <Typography sx={{ textAlign: 'center', color: '#e44258' }}>
          دسترسی ممنوع
        </Typography>
      ) : (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 1.5, mb: 2, alignItems: 'center' }}>
              <TextField
                sx={{ flex: 1, minWidth: 220 }}
                label={entity === 'products' ? 'نام محصول جدید' : `افزودن ${config.label} جدید`}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              {entity === 'products' && (
                <>
                  <Select
                    value={newProductGroupId}
                    onChange={(e) => setNewProductGroupId(e.target.value)}
                    displayEmpty
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="" disabled>انتخاب گروه</MenuItem>
                    <MenuItem value="">بدون گروه</MenuItem>
                    {groups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                    ))}
                  </Select>
                  {/* Assay input removed from management add row */}
                </>
              )}
              <Button startIcon={<AddIcon />} variant="contained" onClick={handleAdd}>
                افزودن
              </Button>
              {entity === 'products' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%', mt: 1, p: 1, borderRadius: 1, bgcolor: 'rgba(50, 51, 56,0.02)' }}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mr: 1 }}>ایجاد سریع گروه:</Typography>
                  <TextField
                    sx={{ minWidth: 200 }}
                    size="small"
                    label="نام گروه"
                    value={quickGroupName}
                    onChange={(e) => setQuickGroupName(e.target.value)}
                  />
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={quickGroupRequiresAssay}
                        onChange={(e) => setQuickGroupRequiresAssay(e.target.checked)}
                      />
                    }
                    label={<Typography sx={{ fontSize: 12 }}>نیاز به عیار</Typography>}
                  />
                  <Button variant="outlined" size="small" onClick={handleQuickAddGroup}>افزودن گروه</Button>
                </Box>
              )}
              {entity === 'products' && (
                <Box sx={{ width: '100%', mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    مدیریت گروه‌ها
                  </Typography>
                  <Paper elevation={0} sx={{ border: '1px solid rgba(50, 51, 56,0.08)', borderRadius: 2 }}>
                    {groups.length === 0 ? (
                      <Typography sx={{ color: 'text.secondary', fontSize: 14, p: 2 }}>گروهی وجود ندارد</Typography>
                    ) : (
                      <List dense>
                        {groups.map((g) => (
                          <ListItem key={g.id}
                            secondaryAction={
                              <Tooltip title="حذف گروه">
                                <IconButton edge="end" color="error" onClick={() => handleDeleteGroup(g.id)}>
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            }
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ fontWeight: 600 }}>{g.name}</Typography>
                                  {g.requiresAssay && (
                                    <Chip size="small" label="نیاز به عیار" color="info" variant="outlined" />
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Paper>
                </Box>
              )}
              {entity === 'product-groups' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={newGroupRequiresAssay}
                      onChange={(e) => setNewGroupRequiresAssay(e.target.checked)}
                    />
                  }
                  label="نیاز به عیار خاک"
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {pagedItems.map((it) => (
                <Box key={it.id} sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 1, alignItems: 'center', p: 1, borderRadius: 1, '&:nth-of-type(odd)': { bgcolor: 'rgba(50, 51, 56,0.02)' } }}>
                  {entity === 'products' ? (
                    <>
                      <Box sx={{ flex: 1, minWidth: 240, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          sx={{ flex: 1 }}
                          value={productEdits[it.id]?.name ?? it.name}
                          onChange={(e) => setProductEdits((prev) => ({
                            ...prev,
                            [it.id]: { ...prev[it.id], name: e.target.value },
                          }))}
                        />
                        {(() => {
                          // Show real-time assay status based on currently selected group
                          const currentGroupId = productEdits[it.id]?.groupId ?? (it.groupId ?? '');
                          const selectedGroup = groups.find(g => g.id === currentGroupId);
                          const requiresAssay = selectedGroup?.requiresAssay || false;
                          
                          return requiresAssay && (
                            <Box sx={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              bgcolor: '#e5f4ff', 
                              color: '#0073ea',
                              px: 1, 
                              py: 0.25, 
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              whiteSpace: 'nowrap'
                            }}>
                              <span>عیار دار</span>
                            </Box>
                          );
                        })()}
                      </Box>
                      <Select
                        value={productEdits[it.id]?.groupId ?? (it.groupId ?? '')}
                        onChange={(e) => {
                          setProductEdits((prev) => ({
                            ...prev,
                            [it.id]: { ...prev[it.id], groupId: e.target.value },
                          }));
                          // Show immediate feedback when group changes
                          const selectedGroup = groups.find(g => g.id === e.target.value);
                          if (selectedGroup?.requiresAssay) {
                            showMessage(`گروه "${selectedGroup.name}" عیار دار است`, 'info');
                          } else if (e.target.value === '') {
                            showMessage('محصول بدون گروه است', 'info');
                          } else {
                            showMessage(`گروه "${selectedGroup?.name || 'نامشخص'}" عیار دار نیست`, 'info');
                          }
                        }}
                        displayEmpty
                        sx={{ minWidth: 220 }}
                      >
                        <MenuItem value="" disabled>انتخاب گروه</MenuItem>
                        <MenuItem value="">بدون گروه</MenuItem>
                        {groups.map((g) => (
                          <MenuItem key={g.id} value={g.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{g.name}</span>
                              {g.requiresAssay && (
                                <Box sx={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center', 
                                  gap: 0.5,
                                  bgcolor: '#e5f4ff', 
                                  color: '#0073ea',
                                  px: 1, 
                                  py: 0.25, 
                                  borderRadius: '8px',
                                  fontSize: '0.7rem',
                                  fontWeight: 500
                                }}>
                                  <span>عیار دار</span>
                                </Box>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {/* Assay input removed from product rows in management UI */}
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="ذخیره تغییرات">
                          <IconButton color="success" onClick={() => handleSave(it.id)}>
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton color="error" onClick={() => handleDelete(it.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  ) : entity === 'product-groups' ? (
                    <>
                      <TextField
                        sx={{ flex: 1, minWidth: 240 }}
                        value={groupEdits[it.id]?.name ?? it.name}
                        onChange={(e) => setGroupEdits((prev) => ({
                          ...prev,
                          [it.id]: { ...prev[it.id], name: e.target.value },
                        }))}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!(groupEdits[it.id]?.requiresAssay ?? it.requiresAssay)}
                            onChange={(e) => setGroupEdits((prev) => ({
                              ...prev,
                              [it.id]: { ...prev[it.id], requiresAssay: e.target.checked },
                            }))}
                          />
                        }
                        label="نیاز به عیار خاک"
                      />
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="ذخیره تغییرات">
                          <IconButton color="success" onClick={() => handleSave(it.id)}>
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton color="error" onClick={() => handleDelete(it.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        value={editMap[it.id] ?? it.name}
                        onChange={(e) => setEditMap((prev) => ({ ...prev, [it.id]: e.target.value }))}
                      />
                      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                        <Tooltip title="ذخیره تغییرات">
                          <IconButton color="success" onClick={() => handleSave(it.id)}>
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton color="error" onClick={() => handleDelete(it.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2 }}>
              <ListPagination
                page={page}
                pageCount={items.length === 0 ? 0 : Math.ceil(items.length / MANAGE_PAGE_SIZE)}
                totalCount={items.length}
                onPageChange={setPage}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageList;


